import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import MenuIcon from "@mui/icons-material/Menu";
import { Toaster } from "react-hot-toast";
import { afmsWorkspaceSx, cc } from "afms/theme/commandCenterTheme";
import EnvironmentalPortalSidebar from "./EnvironmentalPortalSidebar";

const SIDEBAR_WIDTH = 280;

/**
 * The chrome that wraps an Environmental-system app: the green-leaf
 * EnvironmentalPortalSidebar on the left (sticky column on desktop, drawer on
 * mobile) and a flex:1 content area on the right where the routes render.
 *
 * Mounted ONCE at the App.js level when pickSidebarVariant returns
 * "vendorPortal". Page-level layouts (EnvironmentalPortalLayout,
 * CommandCenterLayout) handle only their own header + main content — they
 * don't pick the sidebar.
 *
 * onSwitchSystem is forwarded to EnvironmentalPortalSidebar so the system-name
 * badge (e.g. "Environmental") can open the system picker. The Flex2 logo
 * itself just navigates to the user's home screen.
 */
function EnvironmentalPortalShell({ children, onSwitchSystem }) {
  const { pathname } = useLocation();
  const isDesktop = useMediaQuery("(min-width:1200px)");
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the mobile drawer whenever the user navigates.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    <Box
      className="afms-vendor-shell"
      sx={{
        ...afmsWorkspaceSx,
        display: "flex",
        minHeight: "100vh",
        bgcolor: cc.page,
        color: cc.ink,
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            width: SIDEBAR_WIDTH,
            flexShrink: 0,
            borderRight: "1px solid rgba(255,255,255,0.08)",
            bgcolor: cc.navy,
            // Pin to the viewport. Without this the sidebar grows with
            // the page (its flex sibling = main content), and the bottom
            // org/user card ends up at the bottom of the WHOLE PAGE
            // instead of the bottom of the screen. Sticky + 100vh height
            // keeps the card visible even when the main content scrolls.
            position: "sticky",
            top: 0,
            alignSelf: "flex-start",
            height: "100vh",
            overflowY: "auto",
          }}
        >
          <EnvironmentalPortalSidebar onSwitchSystem={onSwitchSystem} />
        </Box>
      ) : (
        <>
          <IconButton
            aria-label="Open navigation"
            onClick={() => setDrawerOpen(true)}
            sx={{
              position: "fixed",
              top: 12,
              left: 12,
              zIndex: 1300,
              bgcolor: cc.navy,
              color: "#fff",
              boxShadow: "0 10px 20px rgba(6,31,54,0.18)",
              "&:hover": { bgcolor: cc.navy2 },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            PaperProps={{
              sx: { width: SIDEBAR_WIDTH, bgcolor: cc.navy },
            }}
          >
            <EnvironmentalPortalSidebar
              onNavigate={() => setDrawerOpen(false)}
              onSwitchSystem={onSwitchSystem}
            />
          </Drawer>
        </>
      )}
      <Box
        sx={{
          minWidth: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {children}
      </Box>
      <Toaster />
    </Box>
  );
}

EnvironmentalPortalShell.propTypes = {
  children: PropTypes.node,
  onSwitchSystem: PropTypes.func,
};

export default EnvironmentalPortalShell;
